import React, {useEffect, useState} from 'react';
import {Field} from 'formik';
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Typography
} from "@mui/material";
import CampaignMap from "../CampaignMap";
import Button from "@mui/material/Button";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import MapIcon from "@mui/icons-material/Map";
import Constants from "../../../../utils/constants";
import CampaignPatrimoineTable from "./CampaignPatrimoineTable";
import Grid from "@mui/material/Grid2";
import {extractId, useGetPatrimoineDetailQuery} from "../../../../modules/patrimoines/patrimoines.api";
import {toIri} from "../../../../utils/apiResource";


function CampaignPatrimoineField({...props}) {
    const {values, errors, patrimoines} = props;
    const [mode, setMode] = useState(Constants.MODE_LIST);

    // Normalise une valeur (IRI ou id brut) en id numérique stringifié,
    // pour comparer de façon robuste quel que soit le format renvoyé par l'API.
    const normalizeId = (v) => {
        const id = extractId(v);
        return id == null ? '' : String(id);
    };

    const toOrderValue = (container) => {
        const id = extractId(container);
        const numeric = Number(id);
        return Number.isNaN(numeric) ? id : numeric;
    };

    // Valeur normalisée du <Select> : on s'aligne sur la "value" du MenuItem trouvé
    // dans la liste light, afin d'éviter tout mismatch IRI/id en mode édition.
    const currentPatrimoineId = normalizeId(values.patrimoine);
    const matchedPatrimoine = (patrimoines || []).find(
        (m) => normalizeId(m["@id"] || m.id) === currentPatrimoineId
    );
    const selectValue = matchedPatrimoine
        ? (matchedPatrimoine["@id"] || matchedPatrimoine.id)
        : (values.patrimoine || '');

    // Liste light : utilisée uniquement pour alimenter le <Select> (libellé + compteur).
    // Le détail (avec conteneurs) est récupéré à la demande via RTK Query.
    const {
        data: selectedPatrimoine,
        isFetching: detailFetching,
        isError: detailError,
    } = useGetPatrimoineDetailQuery(values.patrimoine, {
        skip: !values.patrimoine,
    });

    const [selectedConteneurs, setSelectedConteneurs] = useState([]);

    // Quand le détail arrive (ou que la sélection change), synchronise la sélection
    // de conteneurs avec les IRIs présents dans `values.conteneurs`.
    useEffect(() => {
        if (!selectedPatrimoine?.conteneurs) {
            setSelectedConteneurs([]);
            return;
        }
        const initialIds = Array.isArray(values.conteneurs)
            ? values.conteneurs.map((c) => normalizeId(typeof c === 'object' ? (c['@id'] || c.id) : c))
            : [];

        let containers = selectedPatrimoine.conteneurs.filter((c) =>
            initialIds.includes(normalizeId(c['@id'] || c.id))
        );

        if (values.ordre?.length > 0 && values.ordre.length === containers.length) {
            containers = [...containers].sort((a, b) => {
                return values.ordre.indexOf(a.id) - values.ordre.indexOf(b.id);
            });
        } else {
            containers = [...containers].sort((a, b) => {
                return parseInt(a.ordreCsv) - parseInt(b.ordreCsv);
            });
        }
        setSelectedConteneurs(containers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPatrimoine, values.conteneurs]);

    const handlePatrimoineSelected = (patrimoineId, form) => {
        // On reset les conteneurs ; le détail sera chargé par RTK Query via le skip flip.
        form.setFieldValue('patrimoine', patrimoineId);
        form.setFieldValue('conteneurs', []);
        form.setFieldValue('ordre', []);
        setSelectedConteneurs([]);
    };

    const handleConteneursSelected = (data, form, field) => {
        const iris = data.map((item) => toIri('conteneurs', item)).filter(Boolean);

        // Une tournée SANS ordre custom doit le rester : l'API retombe alors sur
        // le tri par ordreCsv. On ne reconstruit l'ordre que s'il en existait un,
        // sinon cocher un conteneur figeait un ordre "ids croissants" en BDD
        // (bug introduit par eaa0d2e : ordre rempli avec toute la sélection).
        const hadCustomOrder = (values.ordre || []).length > 0;
        if (hadCustomOrder) {
            const selectedOrderIds = data.map(toOrderValue).filter((id) => id !== null && id !== undefined);
            const selectedOrderKeys = selectedOrderIds.map((id) => String(id));
            // On conserve l'ordre custom existant (nettoyé des conteneurs décochés)
            // et on ajoute les nouveaux conteneurs à la fin.
            const cleanedOrder = (values.ordre || []).filter((id) => selectedOrderKeys.includes(String(id)));
            const missingOrder = selectedOrderIds.filter((id) => !cleanedOrder.some((current) => String(current) === String(id)));
            form.setFieldValue('ordre', [...cleanedOrder, ...missingOrder]);
        }

        form.setFieldValue(field.name, iris);
        setSelectedConteneurs(data);
    };

    const handleModeChange = (value) => {
        setMode(value);
    };

    return (
        <Grid container size={12} spacing={1} className={"sectionContainer"}>
            <Grid size={12}>
                <Typography variant={"h5"} className={"sectionHeader"}>Sélection du patrimoine</Typography>
            </Grid>

            {/* PATRIMOINE FIELD ------------------------------------------------ */}
            <Grid container size={12} alignItems={"center"} className={"mt-2"}>
                <Grid size={3}>
                    Patrimoine <span className="asterix">*</span>
                </Grid>
                <Grid size={9}>
                    <Field
                        type="text"
                        name="patrimoine"
                        value={values.patrimoine}>
                        {({form}) => (
                            <FormControl fullWidth error={Boolean(errors.patrimoine)}>
                                <InputLabel id="label-patrimoine">Sélectionner le patrimoine :</InputLabel>
                                <Select
                                    labelId="label-patrimoine"
                                    label="Sélectionner le patrimoine :"
                                    variant="outlined"
                                    name="patrimoine"
                                    value={selectValue}
                                    onChange={(e) => {
                                        handlePatrimoineSelected(e.target.value, form);
                                    }}
                                >
                                    {patrimoines && patrimoines.map((m) => {
                                        // `conteneursCount` (à fournir côté API list)
                                        // > fallback sur `conteneurs?.length` pour rester
                                        // > compatible avec l'ancienne API.
                                        const count = m.conteneursCount ?? m.conteneurs?.length ?? 0;
                                        // Robustesse : si le groupe `patrimoine:list` côté API
                                        // n'expose pas (encore) `@id`, on retombe sur l'id brut.
                                        // Le query `getPatrimoineDetail` accepte les deux via `extractId`.
                                        const itemValue = m["@id"] || m.id;
                                        return (
                                            <MenuItem key={m.id} value={itemValue}>
                                                {m.libelle} - {m.isUnipatrimoine
                                                ? (m.nombreDeBacs + " bacs")
                                                : (count + " conteneurs")}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                {errors.patrimoine &&
                                    <FormHelperText error={true}>{String(errors.patrimoine)}</FormHelperText>}
                            </FormControl>
                        )}
                    </Field>
                </Grid>
            </Grid>

            {/* DETAIL DU PATRIMOINE SÉLECTIONNÉ -------------------------------- */}
            <Grid container size={12} className={"mt-2"}>
                {values.patrimoine && detailFetching && (
                    <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 1, p: 2}}>
                        <Skeleton variant="text" width="30%"/>
                        <Skeleton variant="rectangular" height={400}/>
                    </Box>
                )}

                {values.patrimoine && detailError && (
                    <Typography color="error" sx={{p: 2}}>
                        Impossible de charger le détail de ce patrimoine.
                    </Typography>
                )}

                {selectedPatrimoine && !detailFetching && !detailError && (
                    <>
                        <Grid container size={12} justifyContent={"center"} spacing={2} className={"mb-2"}>
                            <Grid>
                                <Button
                                    variant={mode === Constants.MODE_LIST ? "contained" : "outlined"}
                                    color="primary"
                                    startIcon={<FormatListNumberedIcon/>}
                                    onClick={() => handleModeChange(Constants.MODE_LIST)}
                                >
                                    Affichage liste
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    variant={mode === Constants.MODE_MAP ? "contained" : "outlined"}
                                    color="primary"
                                    startIcon={<MapIcon/>}
                                    onClick={() => handleModeChange(Constants.MODE_MAP)}
                                >
                                    Affichage carte
                                </Button>
                            </Grid>
                        </Grid>
                        <Typography variant={"h5"}>
                            Conteneurs sélectionnés
                            : {selectedPatrimoine?.isUnipatrimoine
                                ? selectedPatrimoine.nombreDeBacs + ' bacs'
                                : selectedConteneurs.length}
                        </Typography>
                        {mode === Constants.MODE_LIST && (
                            <Field name={"conteneurs"}>
                                {({field, form}) => (
                                    <CampaignPatrimoineTable
                                        conteneurs={selectedPatrimoine.conteneurs}
                                        patrimoine={selectedPatrimoine}
                                        selectedConteneurs={selectedConteneurs}
                                        onConteneursSelected={(data) => {
                                            handleConteneursSelected(data, form, field);
                                        }}/>
                                )}
                            </Field>
                        )}

                        {mode === Constants.MODE_MAP && (
                            <>
                                <Grid size={12}>
                                    <Typography sx={{fontStyle: 'italic', textAlign: 'center'}}>
                                        Entourez les conteneurs d'un polygone pour les ajouter à la tournée,
                                        ou cliquez sur un marqueur pour l'ajouter / le retirer. Les conteneurs
                                        sélectionnés apparaissent en vert.
                                    </Typography>
                                </Grid>
                                <Grid size={3}>
                                    <Grid sx={{maxHeight: '650px', overflowY: 'scroll'}}>
                                        {selectedConteneurs && selectedConteneurs.map((c, i) => (
                                            <p key={i}>{c?.adresse}</p>
                                        ))}
                                    </Grid>
                                </Grid>
                                <Grid size={9}>
                                    <Field name={"conteneurs"}>
                                        {({field, form}) => (
                                            <CampaignMap
                                                conteneurs={selectedPatrimoine.conteneurs}
                                                selectedConteneurs={selectedConteneurs}
                                                patrimoine={selectedPatrimoine}
                                                onConteneursSelected={(data) => {
                                                    handleConteneursSelected(data, form, field);
                                                }}/>
                                        )}
                                    </Field>
                                </Grid>
                            </>
                        )}
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export default CampaignPatrimoineField;
